import type { AuthCredentials, AuthSession } from "./types";

/** Auth token key used in localStorage */
const TOKEN_KEY = "eh_access_token";
const REFRESH_TOKEN_KEY = "eh_refresh_token";
const SESSION_KEY = "eh_auth_session";

export const AUTH_CHANGED_EVENT = "eh:auth-changed";

const authListeners = new Set<() => void>();

/** Cached snapshot for `useSyncExternalStore` — must stay referentially stable between updates. */
let sessionSnapshot: AuthSession | null = null;
let sessionSnapshotLoaded = false;

function notifyAuthChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
  }
  authListeners.forEach((listener) => listener());
}

function readSessionFromStorage(): AuthSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

function writeSession(session: AuthSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function setSessionSnapshot(session: AuthSession | null): void {
  sessionSnapshot = session;
  sessionSnapshotLoaded = true;
}

function loadSessionSnapshot(): AuthSession | null {
  if (!sessionSnapshotLoaded) {
    sessionSnapshot = readSessionFromStorage();
    sessionSnapshotLoaded = true;
  }
  return sessionSnapshot;
}

function expiresAtFromTtl(expiresIn?: number): number | undefined {
  if (expiresIn == null) return undefined;
  return Date.now() + expiresIn * 1000;
}

function buildSession(credentials: AuthCredentials): AuthSession {
  return {
    activeContext: credentials.activeContext,
    availableContexts: credentials.availableContexts,
    mustResetPassword: credentials.mustResetPassword,
    expiresAt: expiresAtFromTtl(credentials.expiresIn),
  };
}

export const auth = {
  getToken: (): string | null => localStorage.getItem(TOKEN_KEY),

  setToken: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
    notifyAuthChanged();
  },

  clearToken: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    notifyAuthChanged();
  },

  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),

  setRefreshToken: (token: string): void => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  clearRefreshToken: (): void => {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  getSession: (): AuthSession | null => loadSessionSnapshot(),

  setSession: (session: AuthSession): void => {
    writeSession(session);
    setSessionSnapshot(session);
    notifyAuthChanged();
  },

  clearSession: (): void => {
    localStorage.removeItem(SESSION_KEY);
    setSessionSnapshot(null);
    notifyAuthChanged();
  },

  /** Persist token + session from login or context-switch response. */
  setAuth: (credentials: AuthCredentials): void => {
    localStorage.setItem(TOKEN_KEY, credentials.accessToken);
    if (credentials.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, credentials.refreshToken);
    }
    const session = buildSession(credentials);
    writeSession(session);
    setSessionSnapshot(session);
    notifyAuthChanged();
  },

  /** Rotate access token on refresh; keep stored permissions (Plan A). */
  updateAccessToken: (accessToken: string, expiresIn?: number): void => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    const session = loadSessionSnapshot();
    if (session) {
      const updated = {
        ...session,
        expiresAt: expiresAtFromTtl(expiresIn) ?? session.expiresAt,
      };
      writeSession(updated);
      setSessionSnapshot(updated);
    }
    notifyAuthChanged();
  },

  /**
   * After change-password + refresh, the new access token no longer carries
   * mustResetPassword. Refresh body omits the flag — clear it on the session.
   */
  clearMustResetPassword: (): void => {
    const session = loadSessionSnapshot();
    if (!session || session.mustResetPassword !== true) return;
    const updated = { ...session, mustResetPassword: false };
    writeSession(updated);
    setSessionSnapshot(updated);
    notifyAuthChanged();
  },

  clearAuth: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(SESSION_KEY);
    setSessionSnapshot(null);
    notifyAuthChanged();
  },

  isAuthenticated: (): boolean => !!localStorage.getItem(TOKEN_KEY),
};

/**
 * Stable session snapshot for `useSyncExternalStore`.
 * Returns the same object reference until auth state mutates.
 */
export function getAuthSessionSnapshot(): AuthSession | null {
  return loadSessionSnapshot();
}

/** Subscribe to auth/session changes (for `useSyncExternalStore`). */
export function subscribeAuthChanges(onStoreChange: () => void): () => void {
  authListeners.add(onStoreChange);
  if (typeof window !== "undefined") {
    window.addEventListener(AUTH_CHANGED_EVENT, onStoreChange);
  }
  return () => {
    authListeners.delete(onStoreChange);
    if (typeof window !== "undefined") {
      window.removeEventListener(AUTH_CHANGED_EVENT, onStoreChange);
    }
  };
}

/** Clears password inputs in the DOM so browsers don't offer to save credentials on navigation. */
export function clearPasswordInputs(root: ParentNode = document): void {
  const inputs = root.querySelectorAll<HTMLInputElement>('input[type="password"]');
  for (let i = 0; i < inputs.length; i += 1) {
    inputs[i].value = "";
    inputs[i].defaultValue = "";
    inputs[i].dispatchEvent(new Event("input", { bubbles: true }));
    inputs[i].dispatchEvent(new Event("change", { bubbles: true }));
  }
}
